import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DetailIcon from '@mui/icons-material/Details';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Box } from '@mui/material';
import { saveData, getData, openDB } from '../../../utils/db';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ResponsiveTable from '../../ResponsiveTable';

function UserIndex() {
  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null); // Track the item ID to delete
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { isAuthenticated, companyId, branchId, userId, logout, token, permissions } = useAuth();

  useEffect(() => {
    // Check if the token is expired
    if (isAuthenticated && tokenIsExpired(token)) {
      // Token is expired, log out the user
      logout();
      return;
    }

  }, [isAuthenticated, companyId]);

  useEffect(() => {
    fetchtableData();
  }, []);

  const fetchtableData = async () => {
    fetchData('UserApi', `/${companyId}/${companyId}`, token)//, token
      .then((response) => {
        if (response.responseCode == '1111') {
          console.log(response);
          setData(response.response_data);
        }
      })
      .catch((error) => {
        console.error('Error fetching User Details:', error);
      });
  };

  const handleDelete = async () => {
    deleteData('UserApi', `${deleteItemId}`, token)//, token
      .then((response) => {
        fetchtableData(); // Refresh data after deletion
        setDeleteItemId(null); // Reset the item ID
        setConfirmDialogOpen(false); // Close the dialog
      })
      .catch((error) => {
        console.error('Error deleting record:', error);
      });

  };

  const handleActivate = (itemId) => {
    activateData('UserApi', `${itemId}`, token)//, token
      .then((response) => {
        fetchtableData(); // Refresh data after deletion
      })
      .catch((error) => {
        // Handle errors for the activation request
      });
  };

  const openConfirmDialog = (itemId) => {
    setDeleteItemId(itemId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setDeleteItemId(null);
    setConfirmDialogOpen(false);
  };

  // Define table columns
  const columns = [
    { field: 'FirstName', header: 'First Name' },
    { field: 'LastName', header: 'Last Name' },
    { field: 'UserName', header: 'User Name' },
    { field: 'ContactNumber', header: 'Contact Number' },
    { field: 'Email', header: 'Email' },
  ];

  // Define action buttons for each row
  const renderActions = (item) => (
    <>
      <Link to={`/userDetail/${item.Id}`}>
        <IconButton size="small">
          <DetailIcon style={{ color: 'darksalmon', fontSize: '19px' }} />
        </IconButton>
      </Link>
      <Link to={`/userEdit/${item.Id}`}>
        <IconButton size="small" color="primary">
          <EditIcon style={{ fontSize: '19px' }} />
        </IconButton>
      </Link>
      <IconButton 
        size="small"
        onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))} 
        color="error"
      >
        {item.IsActive ? (
          <DeleteIcon style={{ fontSize: '19px' }} />
        ) : (
          <ActivateIcon style={{ color: 'darkolivegreen', height: '22px' }} />
        )}
      </IconButton>
    </>
  );

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', sm: '100%', md: '98%' },
        mx: 'auto',
        mt: { xs: 2, md: 6 },
        px: { xs: 2, sm: 2, md: 0 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 2,
          gap: { xs: 2, sm: 0 },
        }}
      >
        <h6
          className='productHead linkcolor'
          style={{
            fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif',
            textTransform: 'uppercase',
            margin: 0,
          }}
        >
          User
        </h6>

        <Button
          variant="contained"
          component={Link}
          to="/userCreate"
          className="linkcolor"
          sx={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '5px 15px',
            border: '1px solid blue',
            textTransform: 'uppercase',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
            height: { xs: '36px', sm: '30px' },
            color: 'blue',
            fontSize: { xs: '12px', sm: '11px' },
            '&:hover': {
              backgroundColor: '#f0f0f0',
            },
          }}
        >
          CREATE NEW
        </Button>
      </Box>

      {/* Responsive Table */}
      <Box sx={{ mb: 5 }}>
        <ResponsiveTable
          columns={columns}
          data={data}
          actions={renderActions}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={closeConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this record?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <button
            onClick={closeConfirmDialog}
            style={{
              backgroundColor: '#dc3545',
              color: '#ffffff',
              border: '1px solid #dc3545',
              borderRadius: '4px',
              padding: '8px 16px',
              cursor: 'pointer',
              marginRight: '8px',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            style={{
              backgroundColor: '#28a745',
              color: '#ffffff',
              border: '1px solid #28a745',
              borderRadius: '4px',
              padding: '8px 16px',
              cursor: 'pointer',
            }}
          >
            Confirm
          </button>
        </DialogActions>
      </Dialog>

      {/* Watermark */}
      <div className="watermark">
        <CopyrightIcon />
        <b style={{ fontFamily: 'Script Bold Italic' }}>EFFTABS SOLUTIONS LLP</b>
      </div>
    </Box>
  );
}

export default UserIndex;

